import type { TwoColumnLayoutV2Handle } from "@monthly/charts";
import { TitleWithFilters, type TitleWithFiltersProps } from "@monthly/ui";
import {
  BarChart,
  Fullscreen,
  TableChartOutlined,
  Timeline,
  TrendingUp,
} from "@mui/icons-material";
import { Box, Card, Stack, styled, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { useImperativeHandle, useRef, useState } from "react";

import { IconButton } from "../icon-button/IconButton";
import { ANALYTIC_TABLE_TITLE } from "../table-insight/constants";
import {
  type TableInsight,
  TableInsightAction,
} from "../table-insight/TableInsightAction";
import { Title } from "../title/Title";
import { FullScreenDialog } from "./FullScreenDialog";

export const TwoColumnLayout = styled(Stack)({
  display: "grid",
  gridTemplateColumns: "2fr 1fr",
  gap: 16,
});

export const SectionCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: 12,
  boxShadow: theme.shadows[4],
  padding: 16,
  marginBottom: 0,
  transition: "all 0.2s ease-out",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[5],
  },
}));

export interface ScrollableTableProps {
  children: ReactNode;
  maxRows?: number;
}

export interface TwoColumnLayoutV2Props {
  period: string;
  title: string;
  titleIcon?: ReactNode;
  tableDescription: string;
  subtitle: string;
  currency: string;
  units: string;
  verticalFlowNetData?: number[];
  onViewChange?: (view: "primary" | "vertical") => void;
  onDialogDownload?: () => void;
  currentView?: "primary" | "vertical";
  showActionButtons?: boolean;
  hasTimeline?: boolean;
  hasTrendingUp?: boolean;
  hasFullscreen?: boolean;
  legendItems?: ReactNode;
  useFilters?: TitleWithFiltersProps["useFilters"];
  /** Mara insights of the table. The Ask Mara icon is shown only when provided. */
  tableInsight?: TableInsight;
  children: {
    chart: ReactNode;
    table: ReactNode;
  };
  ref: React.Ref<TwoColumnLayoutV2Handle>;
}

export const DEFAULT_MAX_ROWS = 7.5;
export const TABLE_ROW_HEIGHT = 40;

export function ScrollableTable({
  children,
  maxRows = DEFAULT_MAX_ROWS,
}: ScrollableTableProps) {
  return (
    <Stack
      sx={{
        mt: 2,
        maxHeight: `calc(${maxRows} * ${TABLE_ROW_HEIGHT}px)`,
        overflow: "auto",
      }}
    >
      {children}
    </Stack>
  );
}

export function TwoColumnLayoutV2({
  period,
  title,
  titleIcon = (
    <BarChart sx={(theme) => ({ color: theme.palette.primary.light })} />
  ),
  tableDescription,
  subtitle,
  currency,
  units,
  onViewChange,
  currentView = "primary",
  showActionButtons = true,
  legendItems,
  children,
  ref,
  hasTimeline = true,
  hasTrendingUp = true,
  hasFullscreen = true,
  onDialogDownload,
  useFilters,
  tableInsight,
}: TwoColumnLayoutV2Props) {
  const [isFullscreenIsOpen, setIsFullscreenOpen] = useState(false);

  const handleTimelineClick = () => {
    onViewChange?.("primary");
  };

  const handleTrendingClick = () => {
    onViewChange?.("vertical");
  };

  const handleOpenFullscreen = () => setIsFullscreenOpen(true);
  const handleCloseFullscreen = () => setIsFullscreenOpen(false);

  const chartRef = useRef<HTMLDivElement>(null);
  const chartAndTableRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    getChartRef: () => chartRef.current,
    getChartAndTableRef: () => chartAndTableRef.current,
  }));

  return (
    <Stack gap={2}>
      <FullScreenDialog
        currency={currency}
        title={title}
        subtitle={subtitle}
        period={period}
        children={children}
        onDownload={onDialogDownload}
        isFullscreenIsOpen={isFullscreenIsOpen}
        handleCloseFullscreen={handleCloseFullscreen}
        units={units}
      />
      <TwoColumnLayout ref={chartAndTableRef}>
        {/* Left Card - Chart */}
        <SectionCard ref={chartRef}>
          <Stack gap={3}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
                width: "100%",
                gap: { xs: 2, sm: 0 },
              }}
            >
              {/* Title Section */}
              <TitleWithFilters
                title={title}
                subtitle={subtitle}
                icon={titleIcon}
                useFilters={useFilters}
              />
              {showActionButtons && (
                <Stack
                  direction="row"
                  spacing={{ xs: 1.5, sm: 2 }}
                  alignSelf="flex-start"
                >
                  {hasTimeline && (
                    <IconButton
                      onClick={handleTimelineClick}
                      isSelected={currentView === "primary"}
                      tooltip="Tendencia"
                    >
                      <Timeline fontSize="small" />
                    </IconButton>
                  )}

                  {hasTrendingUp && (
                    <IconButton
                      onClick={handleTrendingClick}
                      isSelected={currentView === "vertical"}
                      tooltip="Crecimiento"
                    >
                      <TrendingUp fontSize="small" />
                    </IconButton>
                  )}

                  {hasFullscreen && (
                    <IconButton
                      onClick={handleOpenFullscreen}
                      isSelected={isFullscreenIsOpen}
                      tooltip="Fullscreen"
                    >
                      <Fullscreen fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              )}
            </Box>
            {legendItems && (
              <Stack
                direction="row"
                gap={2}
                alignItems="center"
                alignSelf="flex-end"
                justifyContent="flex-end"
              >
                {legendItems}
              </Stack>
            )}
            {children.chart}
          </Stack>
        </SectionCard>

        {/* Right Card - Table */}
        <SectionCard>
          <Stack gap={1}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Title
                icon={<TableChartOutlined />}
                title={ANALYTIC_TABLE_TITLE}
              />
              {tableInsight && (
                <TableInsightAction
                  title={ANALYTIC_TABLE_TITLE}
                  {...tableInsight}
                />
              )}
            </Stack>

            <Typography variant="overline" color="text.primary">
              {tableDescription}
            </Typography>
          </Stack>

          <ScrollableTable>{children.table}</ScrollableTable>
        </SectionCard>
      </TwoColumnLayout>
    </Stack>
  );
}
